import React, { useEffect, useState } from "react";
import axios from "axios";
import { LogOut, Loader2 } from "lucide-react";
import NotificationBell from "../components/NotificationBell";
import {
  TABS,
  myAreas,
  API,
  TabWithExport,
  TOKEN_KEY,
  ROLE_KEY,
  ROLE_LABEL_KEY,
  AREAS_KEY,
} from "./admin/shared";
import { Login } from "./admin/Login";
import { DashboardTab } from "./admin/DashboardTab";
import { OverviewTab } from "./admin/OverviewTab";
import { AttendTab, ConsultationsTab, ChatLeadsTab, NewsletterTab } from "./admin/ListsTabs";
import { GroupsTab } from "./admin/GroupsTab";
import { AccessCodesTab } from "./admin/CodesTab";
import { ClientRecordsTab } from "./admin/RecordsTab";
import { DirectChatsTab } from "./admin/ChatsTab";
import { AppointmentsTab } from "./admin/AppointmentsTab";
import { ScheduleTab } from "./admin/ScheduleTab";
import { BookingTab } from "./admin/BookingTab";
import { ClinicsTab } from "./admin/ClinicsTab";
import { PaymentsTab } from "./admin/PaymentsTab";
import { ReportsTab } from "./admin/ReportsTab";
import { TeamTab } from "./admin/TeamTab";
import { AuditTab } from "./admin/AuditTab";
import { ClientsTab } from "./admin/ClientsTab";
import { InvoicesTab } from "./admin/InvoicesTab";
import { EmailsTab } from "./admin/EmailsTab";
import { NotesTab } from "./admin/NotesTab";

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [tab, setTab] = useState("dashboard");
  const [roleLabel, setRoleLabel] = useState(localStorage.getItem(ROLE_LABEL_KEY) || "Owner");
  const [areas, setAreas] = useState(myAreas());
  const [data, setData] = useState({
    stats: null,
    attend: [],
    consultations: [],
    chat_leads: [],
    newsletter: [],
  });
  const [loading, setLoading] = useState(false);

  const authHeader = () => {
    const t = localStorage.getItem(TOKEN_KEY);
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ROLE_KEY);
    localStorage.removeItem(ROLE_LABEL_KEY);
    localStorage.removeItem(AREAS_KEY);
    setAuthed(false);
  };

  const fetchAll = async () => {
    setLoading(true);
    const can = (a) => areas.includes(a);
    const jobs = {};
    if (can("overview")) jobs.stats = axios.get(`${API}/admin/stats`, { headers: authHeader() });
    if (can("attend")) jobs.attend = axios.get(`${API}/admin/attend-requests`, { headers: authHeader() });
    if (can("consultations")) jobs.consultations = axios.get(`${API}/admin/consultations`, { headers: authHeader() });
    if (can("chat_leads")) jobs.chat_leads = axios.get(`${API}/admin/chat-leads`, { headers: authHeader() });
    if (can("newsletter")) jobs.newsletter = axios.get(`${API}/admin/newsletter`, { headers: authHeader() });
    try {
      const keys = Object.keys(jobs);
      const res = await Promise.all(keys.map((k) => jobs[k]));
      const d = { stats: null, attend: [], consultations: [], chat_leads: [], newsletter: [] };
      keys.forEach((k, i) => { d[k] = res[i].data; });
      setData(d);
    } catch (e) {
      if (e?.response?.status === 401) logout();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) {
      setRoleLabel(localStorage.getItem(ROLE_LABEL_KEY) || "Owner");
      setAreas(myAreas());
      fetchAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  useEffect(() => {
    if (!areas.includes(tab)) setTab("dashboard");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areas]);

  // Deep link: /admin?tab=<key> (e.g. after Google Calendar OAuth redirect)
  useEffect(() => {
    const wanted = new URLSearchParams(window.location.search).get("tab");
    if (wanted) setTab(wanted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!authed) return <Login onSuccess={() => setAuthed(true)} />;

  const visibleTabs = TABS.filter((t) => areas.includes(t.area || t.key));

  return (
    <div className="min-h-screen bg-[#F8F7F3]" data-testid="admin-dashboard">
      <header className="bg-[#3F5043] text-[#F8F7F3] px-5 sm:px-8 py-5 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="font-serif italic text-2xl">Soarlife</div>
            <span className="text-[11px] uppercase tracking-[0.25em] text-[#AE8F78] font-semibold">
              Admin
            </span>
            {roleLabel !== "Owner" && (
              <span
                data-testid="admin-role-badge"
                className="text-[10px] uppercase tracking-[0.15em] font-semibold bg-[#D4AF37]/20 text-[#D4AF37] rounded-full px-3 py-1"
              >
                {roleLabel}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell authHeader={authHeader} basePath="/admin/notifications" dark />
            <a
              href="/"
              className="text-xs uppercase tracking-[0.22em] text-[#F8F7F3]/70 hover:text-[#F8F7F3]"
            >
              View Site
            </a>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F8F7F3]/10 text-[#F8F7F3] text-xs hover:bg-[#F8F7F3]/20"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <nav className="bg-[#EDEDEE] border-b border-[#BCB3A6]/40 px-5 sm:px-8 overflow-x-auto">
        <div className="max-w-7xl mx-auto flex gap-1 sm:gap-2">
          {visibleTabs.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                data-testid={`admin-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`px-4 py-3 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
                  active
                    ? "border-[#875F45] text-[#3F5043]"
                    : "border-transparent text-[#3F5043]/60 hover:text-[#3F5043]"
                }`}
              >
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        {loading && (
          <div className="text-center text-[#899480] py-8">
            <Loader2 size={20} className="animate-spin inline" /> Loading…
          </div>
        )}

        {tab === "dashboard" && <DashboardTab authHeader={authHeader} onNavigate={setTab} />}
        {tab === "overview" && data.stats && (
          <OverviewTab stats={data.stats} />
        )}
        {tab === "attend" && (
          <TabWithExport source="attend_requests" title="Attend With Me">
            <AttendTab items={data.attend} />
          </TabWithExport>
        )}
        {tab === "consultations" && (
          <TabWithExport source="consultations" title="Consultations">
            <ConsultationsTab items={data.consultations} />
          </TabWithExport>
        )}
        {tab === "chat_leads" && (
          <TabWithExport source="chat_leads" title="Chat Leads">
            <ChatLeadsTab items={data.chat_leads} />
          </TabWithExport>
        )}
        {tab === "newsletter" && (
          <TabWithExport source="newsletter" title="Newsletter">
            <NewsletterTab items={data.newsletter} />
          </TabWithExport>
        )}
        {tab === "codes" && <AccessCodesTab authHeader={authHeader} />}
        {tab === "records" && <ClientRecordsTab authHeader={authHeader} />}
        {tab === "chats" && <DirectChatsTab authHeader={authHeader} />}
        {tab === "appointments" && <AppointmentsTab authHeader={authHeader} />}
        {tab === "schedule" && <ScheduleTab authHeader={authHeader} />}
        {tab === "booking" && <BookingTab authHeader={authHeader} />}
        {tab === "reports" && <ReportsTab authHeader={authHeader} />}
        {tab === "groups" && (
          <GroupsTab authHeader={authHeader} />
        )}
        {tab === "clinics" && <ClinicsTab authHeader={authHeader} />}
        {tab === "payments" && <PaymentsTab authHeader={authHeader} />}
        {tab === "staff" && <TeamTab authHeader={authHeader} />}
        {tab === "audit" && <AuditTab authHeader={authHeader} />}
        {tab === "clients" && <ClientsTab authHeader={authHeader} />}
        {tab === "invoices" && <InvoicesTab authHeader={authHeader} />}
        {tab === "emails" && <EmailsTab authHeader={authHeader} />}
        {tab === "notes" && <NotesTab authHeader={authHeader} />}
      </main>
    </div>
  );
}
